import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useOrders } from "@/hooks/useOrders";
import { useAuth } from "@/contexts/AuthContext";
import { getStatusConfig } from "@/components/orders/statusConfig";
import { sortActiveOrdersByUrgency } from "@/utils/sortOrders";
import { NewOrderAlert } from "@/components/orders/NewOrderAlert";
import { OrdersHeaderBar } from "@/components/orders/OrdersHeaderBar";
import { OrdersListPanel } from "@/components/orders/OrdersListPanel";
import { OrderDetail } from "@/components/orders/OrderDetail";
import { VentaDirecta } from "@/components/orders/VentaDirecta";
import { CloseCaixaModal } from "@/components/financiero/CloseCaixaModal";
import { ChannelModal } from "@/components/orders/ChannelModal";
import { DeliveryTimeModal } from "@/components/orders/DeliveryTimeModal";
import { WABanner } from "@/components/layout/WABanner";
import { KarolOperationalBanner } from "@/components/karol/KarolOperationalBanner";
import { stopBellSound } from "@/lib/bellSound";
import { toast } from "sonner";
import { ShoppingBag, ChevronLeft } from "lucide-react";
export default function OrdersPage() {
  const { t } = useTranslation();
  const { isOwner, authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const {
    orders, deliverers, cashRegister, loading,
    selectedOrder, setSelectedOrder,
    newOrderAlert, dismissAlert,
    fetchData, toggleCaja, quickAdvanceStatus, optimisticHideOrder, handlePrintAllPending,
    stats,
  } = useOrders();

  const [filter, setFilter] = useState("todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [showVentaDirecta, setShowVentaDirecta] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(40);
  const [pickupTime, setPickupTime] = useState(20);
  const [restaurant, setRestaurant] = useState(null);
  const [showCloseCaixa, setShowCloseCaixa] = useState(false);
  const [channelModalMode, setChannelModalMode] = useState(null); // "close"|"open"|null
  const [showDeliveryTime, setShowDeliveryTime] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [showOpenCaixa, setShowOpenCaixa] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [groupByStatus] = useState(true);

  const HISTORY_LIMIT = 50;
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  // Load restaurant settings to initialise delivery status and wait times
  useEffect(() => {
    authAxiosRef.current.get("/restaurant").then(r => {
      const rest = r.data;
      setRestaurant(rest);
      if (rest.wait_time_min) setDeliveryTime(rest.wait_time_min);
      if (rest.wait_time_pickup) setPickupTime(rest.wait_time_pickup);
      else if (rest.wait_time_min) setPickupTime(rest.wait_time_min);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (filter !== "historico") return;
    setHistoryLoading(true);
    setHistoryError(false);
    authAxiosRef.current.get(`/orders/history?limit=${HISTORY_LIMIT}&offset=${historyOffset}`)
      .then(res => {
        setHistoryOrders(res.data.orders || []);
        setHistoryTotal(res.data.total || 0);
      })
      .catch((err) => {
        console.error("[OrdersPage] /orders/history failed:", err?.response?.status, err?.message);
        setHistoryError(true);
      })
      .finally(() => setHistoryLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, historyOffset]);

  const statusConfig = useMemo(() => getStatusConfig(t), [t]);

  const isToday = useCallback((dateStr) => new Date(dateStr).toDateString() === new Date().toDateString(), []);

  const filteredOrders = useMemo(() => {
    const result = orders.filter(o => {
      if (filter === "hoy") return isToday(o.created_at) && !o.hidden && o.status !== "cancelled";
      if (filter === "agendados") return o.scheduled_for && !o.hidden && !["delivered", "cancelled", "rejected"].includes(o.status);
      if (filter === "cancelados") return o.status === "cancelled";
      if (filter === "ocultos") return o.hidden;
      return !o.hidden && o.status !== "cancelled";
    }).filter(o => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase().replace("#", "");
      return (o.customer_name || "").toLowerCase().includes(q) ||
             o.id.toLowerCase().includes(q) ||
             (o.customer_phone || "").includes(q) ||
             o.id.substring(0, 8).toUpperCase().toLowerCase().includes(q) ||
             String(o.order_number || "").includes(q);
    });
    return sortActiveOrdersByUrgency(result);
  }, [orders, filter, searchQuery, isToday]);

  const groupedOrders = useMemo(() => {
    if (!groupByStatus || filter === "ocultos") return null;
    const statusOrder = ["scheduled", "pending", "awaiting_payment", "preparing", "ready", "delivering", "delivered", "cancelled", "rejected"];
    const groups = {};
    for (const status of statusOrder) {
      const statusOrders = filteredOrders.filter(o => o.status === status);
      if (statusOrders.length > 0) groups[status] = statusOrders;
    }
    return groups;
  }, [filteredOrders, groupByStatus, filter]);

  const navigateOrder = (direction) => {
    if (!selectedOrder) return;
    const idx = filteredOrders.findIndex(o => o.id === selectedOrder.id);
    const newIdx = idx + direction;
    if (newIdx >= 0 && newIdx < filteredOrders.length) setSelectedOrder(filteredOrders[newIdx]);
  };

  const handleDismissAndView = () => {
    dismissAlert();
    const order = orders.find(o => o.id === newOrderAlert?.id);
    if (order) setSelectedOrder(order);
  };

  // FECHAR CAIXA → always goes directly to CloseCaixaModal (no channel detour)
  const handleToggleCaixa = () => {
    if (cashRegister) {
      setShowCloseCaixa(true);
    } else {
      setOpeningAmount("");
      setShowOpenCaixa(true);
    }
  };

  // Channel badge click → open or close modal based on current channel state
  const handleChannelClick = () => {
    const deliveryOn = restaurant?.delivery_enabled !== false;
    const pickupOn   = restaurant?.pickup_enabled !== false;
    const allClosed  = !deliveryOn && !pickupOn;
    setChannelModalMode(allClosed ? "open" : "close");
  };

  // Called when user confirms an action inside ChannelModal
  // channel: "delivery"|"pickup"|"all" — enabled: true (open) | false (close)
  const handleChannelToggle = async (channel, enabled) => {
    const payload = {};
    if (channel === "delivery" || channel === "all") payload.delivery_enabled = enabled;
    if (channel === "pickup"   || channel === "all") payload.pickup_enabled   = enabled;
    try {
      await authAxios.patch("/restaurant", payload);
      setRestaurant(prev => ({ ...prev, ...payload }));
      const labels = {
        delivery: enabled ? "Delivery aberto"    : "Delivery fechado",
        pickup:   enabled ? "Retirada aberta"    : "Retirada fechada",
        all:      enabled ? "Restaurante aberto" : "Restaurante fechado",
      };
      toast.success(labels[channel] || (enabled ? "Canal aberto" : "Canal fechado"));
    } catch {
      toast.error("Erro ao alterar canal. Tente novamente.");
    }
  };

  const handleSaveDeliveryTime = async ({ delivery, pickup }) => {
    setSavingTime(true);
    try {
      await authAxios.patch("/restaurant", {
        wait_time_min: delivery,
        wait_time_max: delivery,
        wait_time_pickup: pickup,
      });
      setDeliveryTime(delivery);
      setPickupTime(pickup);
      setRestaurant(prev => ({ ...prev, wait_time_min: delivery, wait_time_max: delivery, wait_time_pickup: pickup }));
      toast.success(`Tempo atualizado: Delivery ${delivery}min · Pickup ${pickup}min`);
      setShowDeliveryTime(false);
    } catch {
      toast.error("Erro ao salvar tempo. Tente novamente.");
    } finally {
      setSavingTime(false);
    }
  };

  const handleOpenCaixa = async () => {
    const amount = parseFloat(openingAmount) || 0;
    await toggleCaja(amount);
    setShowOpenCaixa(false);
  };

  if (showVentaDirecta) {
    return (
      <MainLayout>
        <VentaDirecta onClose={() => setShowVentaDirecta(false)} onCreated={() => { setShowVentaDirecta(false); fetchData(); }} />
      </MainLayout>
    );
  }

  const selectedIdx = selectedOrder ? filteredOrders.findIndex(o => o.id === selectedOrder.id) : -1;

  return (
    <MainLayout>
      <div className="flex flex-col h-[calc(100svh-80px)]" data-testid="orders-page">
        <NewOrderAlert alert={newOrderAlert} onDismiss={dismissAlert} onView={handleDismissAndView} />
        <KarolOperationalBanner />
        <WABanner showAlways />

        {/* Hide global header on mobile when an order is open — irrelevant there */}
        <div className={selectedOrder ? "hidden md:block" : ""}>
          <OrdersHeaderBar
            stats={stats}
            restaurant={restaurant}
            deliveryTime={deliveryTime}
            cashRegister={cashRegister}
            isOwner={isOwner}
            onChannelClick={handleChannelClick}
            onEditDeliveryTime={() => setShowDeliveryTime(true)}
            onPrintAll={handlePrintAllPending}
            onToggleCaja={handleToggleCaixa}
          />
        </div>

        <div className="flex flex-1 overflow-x-hidden flex-col md:flex-row md:overflow-hidden">
          <OrdersListPanel
            loading={loading}
            orders={orders}
            filteredOrders={filteredOrders}
            groupedOrders={groupedOrders}
            groupByStatus={groupByStatus}
            filter={filter}
            searchQuery={searchQuery}
            stats={stats}
            deliverers={deliverers}
            selectedOrderId={selectedOrder?.id}
            newOrderAlertId={newOrderAlert?.id}
            statusConfig={statusConfig}
            onFilterChange={(f) => { setFilter(f); setHistoryOffset(0); }}
            onSearchChange={setSearchQuery}
            onSelectOrder={(order) => { stopBellSound(); dismissAlert(); setSelectedOrder(order); }}
            onAdvance={quickAdvanceStatus}
            dismissAlert={dismissAlert}
            isToday={isToday}
            historyOrders={historyOrders}
            historyLoading={historyLoading}
            historyError={historyError}
            historyTotal={historyTotal}
            historyOffset={historyOffset}
            historyLimit={HISTORY_LIMIT}
            onHistoryPageChange={setHistoryOffset}
          />

          {/* Right Panel - Order Detail */}
          <div className={`flex-1 bg-white min-w-0 ${selectedOrder ? "flex" : "hidden md:flex"}`} data-testid="order-detail-area">
            {selectedOrder ? (
              <div className="flex-1 flex flex-col min-w-0">
                {/* Mobile back button — outside scroll, stays visible at top */}
                <div className="md:hidden px-2 py-1 border-b bg-gray-50 flex items-center shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => setSelectedOrder(null)} data-testid="back-to-list-btn">
                    <ChevronLeft className="w-3.5 h-3.5" /> Voltar
                  </Button>
                </div>
                {/* Mobile: single scroll container; Desktop: overflow-hidden passthrough */}
                <div className="flex-1 overflow-y-auto md:overflow-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <OrderDetail
                    key={selectedOrder.id}
                    order={selectedOrder}
                    deliverers={deliverers}
                    onUpdate={fetchData}
                    onNavigate={navigateOrder}
                    orderIndex={selectedIdx}
                    totalOrders={filteredOrders.length}
                    onClearSelection={() => setSelectedOrder(null)}
                    onOptimisticHide={optimisticHideOrder}
                  />
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <ShoppingBag className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">Selecione um pedido</p>
                <p className="text-sm">Clique em um pedido da lista</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Abrir Caixa modal — asks for opening amount */}
      <Dialog open={showOpenCaixa} onOpenChange={setShowOpenCaixa}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Abrir Caixa</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">Quanto dinheiro está no caixa agora?</p>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00 €"
              value={openingAmount}
              onChange={e => setOpeningAmount(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === "Enter" && handleOpenCaixa()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenCaixa(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleOpenCaixa}>Abrir Caixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fechar Caixa modal — shows totals before closing */}
      <CloseCaixaModal
        open={showCloseCaixa}
        onClose={() => setShowCloseCaixa(false)}
        authAxios={authAxios}
        onSuccess={() => { toggleCaja(); fetchData(); }}
      />

      {/* Channel modal — close or open delivery/pickup channels */}
      <ChannelModal
        open={channelModalMode !== null}
        onClose={() => setChannelModalMode(null)}
        mode={channelModalMode ?? "close"}
        restaurant={restaurant}
        onToggle={handleChannelToggle}
      />

      {/* Delivery time modal — persists time to public menu */}
      <DeliveryTimeModal
        open={showDeliveryTime}
        onClose={() => setShowDeliveryTime(false)}
        deliveryTime={deliveryTime}
        pickupTime={pickupTime}
        onSave={handleSaveDeliveryTime}
        saving={savingTime}
      />
    </MainLayout>
  );
}
